'use client';

import type { MissionsGroupVariants } from '../../types';
import type { UserQuestDto } from '@/queries/userQuest/userQuest.schema';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GmMissionCard } from '@/components/entities/gamification/GmMissionCard';
import { GmMissionsSkeleton } from '@/components/entities/gamification/skeletons/GmMissionsSkeleton';
import { Icon } from '@/components/entities/Sprite';
import { useTimer } from '@/hooks/useTimer';
import { AppRoutes } from '@/i18n/constants';
import { useRouter } from '@/i18n/routing';
import { cn } from '@/utils/cn';
import { pad } from '@/utils/parsing.utils';
import { buildMissionGamesFilters, buildMissionGamesSearchParams } from '../../utils/missionGamesFilters';
import { MissionsGroupEmptyState } from './components/MissionGroupEmptyState';
import { shouldHideCompletedWelcomeMissions, shouldShowWelcomeCompletedState } from './components/welcomeMission.utils';
import { WelcomeMissions } from './components/WelcomeMissions';

function formatTimeLeft(timeComponents: { hours: number; minutes: number; seconds: number } | null) {
  if (!timeComponents) {
    return undefined;
  }

  const { hours, minutes, seconds } = timeComponents;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (days > 0) {
    const daysString = `${days}d`;
    const hoursString = remainingHours > 0 && `${remainingHours}h`;
    const minutesString = minutes && `${minutes}m`;
    return [daysString, hoursString, minutesString]
      .filter(Boolean)
      .join(' ');
  }

  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

interface Props {
  variant: MissionsGroupVariants;
  missions?: UserQuestDto[];
  loading?: boolean;
  onExpired?: VoidFunction;
  nextUpdate?: number | Date;
}

export const MissionsGroup = ({ loading, onExpired, variant, missions = [], nextUpdate }: Props) => {
  const t = useTranslations('gamification');
  const router = useRouter();
  const expiredTargetTimestampRef = useRef<number | null>(null);
  const [updateTime, setUpdateTime] = useState<Date | null>(null);

  const targetTimestamp = typeof nextUpdate === 'number' ? nextUpdate : (nextUpdate?.getTime() ?? null);
  const { timeComponents, isExpired } = useTimer({ targetDate: updateTime });

  useEffect(() => {
    if (targetTimestamp === null) {
      expiredTargetTimestampRef.current = null;
      setUpdateTime(null);
      return;
    }

    if (expiredTargetTimestampRef.current === targetTimestamp) {
      return;
    }

    setUpdateTime(new Date(targetTimestamp));
  }, [targetTimestamp]);

  useEffect(() => {
    if (isExpired && updateTime) {
      expiredTargetTimestampRef.current = updateTime.getTime();
      setUpdateTime(null);
      onExpired?.();
    }
  }, [isExpired, onExpired, updateTime]);

  const timeLeft = formatTimeLeft(timeComponents);
  const title = t(`mission-group.${variant}-missions.title`);

  const isWelcomeMissions = variant === 'welcome';
  const hasNoActiveMissions = !missions.length || missions.every((mission) => {
    const done = mission.progress ?? 0;
    const total = mission.quest.progress.target ?? 0;
    return done === total;
  });

  const handleCardAction = useCallback((filtersSearchParams: string) => {
    router.push(`${AppRoutes.gamificationMissionGames}?${filtersSearchParams}`);
  }, [router]);

  const renderMissions = () => {
    if (isWelcomeMissions) {
      if (shouldShowWelcomeCompletedState(missions)) {
        return <MissionsGroupEmptyState variant={variant} />;
      }

      return <WelcomeMissions missions={missions} />;
    }

    if (hasNoActiveMissions) {
      return <MissionsGroupEmptyState variant={variant} />;
    }

    return missions.map((task) => {
      const missionGamesFilters = buildMissionGamesFilters(task.quest.filters);
      const missionGamesSearchParams = buildMissionGamesSearchParams(missionGamesFilters).toString();

      return (
        <GmMissionCard
          key={task.id}
          {...task}
          onAction={missionGamesSearchParams ? () => handleCardAction(missionGamesSearchParams) : undefined}
        />
      );
    });
  };

  if (loading) {
    return <GmMissionsSkeleton variant={variant} />;
  }

  if (isWelcomeMissions && (missions.length === 0 || shouldHideCompletedWelcomeMissions(missions))) {
    return null;
  }

  return (
    <div className="flex flex-col w-full gap-3">
      <div className="flex justify-between md:justify-start gap-3 items-center">
        <p className="lg:leading-5.5 text-sm leading-5 lg:text-lg font-medium text-light text-start">
          {title}
        </p>
        {
          nextUpdate && timeLeft && (
            <div className="flex text-main-pending justify-center items-center gap-1">
              <Icon name="clock" className="size-4" />
              <p className="text-sm leading-5">{`${t('mission-group.next-in')} ${timeLeft}`}</p>
            </div>
          )
        }
      </div>

      <div className={cn('lg:grid flex flex-col gap-2 lg:gap-3', hasNoActiveMissions || isWelcomeMissions ? 'lg:grid-cols-1' : 'lg:grid-cols-3')}>
        {renderMissions()}
      </div>
    </div>
  );
};
